/**
 * Detect iOS browsers that are WKWebView hosts rather than Safari itself, such
 * as Bluefy. They report `'download' in HTMLAnchorElement.prototype === true`,
 * but the host app has no download handler, so clicking a `blob:` anchor is
 * silently dropped and the user gets no file at all.
 *
 * Safari and Chrome for iOS both carry a `Safari/` token and do handle the
 * anchor download, so they deliberately stay on the standard path.
 * @returns {boolean} True if anchor-based downloads are expected to fail.
 */
const isIOSWebViewHost = () => {
    const ua = window.navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !/Safari\//.test(ua);
};

/**
 * The standard download path: click a generated anchor pointing at a blob URL.
 * @param {string} filename Name to save the file under.
 * @param {Blob} blob File contents.
 */
const anchorDownload = (filename, blob) => {
    const downloadLink = document.createElement('a');
    document.body.appendChild(downloadLink);

    // Use special ms version if available to get it working on Edge.
    if (navigator.msSaveOrOpenBlob) {
        navigator.msSaveOrOpenBlob(blob, filename);
        return;
    }

    if ('download' in HTMLAnchorElement.prototype) {
        const url = window.URL.createObjectURL(blob);
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.type = blob.type;
        downloadLink.click();
        // remove the link after a timeout to prevent a crash on iOS 13 Safari
        window.setTimeout(() => {
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
        }, 1000);
    } else {
        // iOS 12 Safari, open a new page and set href to data-uri
        let popup = window.open('', '_blank');
        const reader = new FileReader();
        reader.onloadend = function () {
            popup.location.href = reader.result;
            popup = null;
        };
        reader.readAsDataURL(blob);
    }
};

/**
 * Hand the file to the OS share sheet, which on iOS offers "Save to Files".
 * @param {string} filename Name to save the file under.
 * @param {Blob} blob File contents.
 * @returns {Promise|null} A promise that rejects if sharing failed, or null if
 *   sharing this file is not possible at all.
 */
const shareFile = (filename, blob) => {
    if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
        return null;
    }
    const file = new File([blob], filename, {type: blob.type});
    if (!navigator.canShare({files: [file]})) {
        return null;
    }
    return navigator.share({files: [file]});
};

export default (filename, blob) => {
    if (isIOSWebViewHost()) {
        const sharing = shareFile(filename, blob);
        if (sharing) {
            sharing.catch(err => {
                // The user dismissing the share sheet is not a failure, and
                // re-triggering a download after it would be surprising.
                if (err && err.name === 'AbortError') {
                    return;
                }
                anchorDownload(filename, blob);
            });
            return;
        }
    }

    anchorDownload(filename, blob);
};
